<template>
	<div class="content">
		<header class="page-header">
			<span class="page-header-emoji">🔑</span>
			<div class="page-header-text">
				<div class="page-header-title">设备密钥</div>
				<div class="page-header-sub">{{ headerSubText }}</div>
			</div>
		</header>

		<div class="page-tabs">
			<nav class="tab-bar">
				<button
					v-for="tab in tabItems"
					:key="tab.name"
					type="button"
					class="tab-item"
					:class="{ active: activeTab === tab.name }"
					@click="activeTab = tab.name"
				>
					{{ tab.label }}
				</button>
			</nav>

			<div v-show="activeTab === 'keys'" class="tab-panel">
				<section class="add-section">
					<div class="add-section-head">
						<span class="add-title">新建密钥</span>
						<button type="button" class="link-btn" @click="regenerateKey">换一换</button>
					</div>
					<div class="key-preview" @click="copyKey(generatedKey)">
						<div class="key-preview-text">{{ generatedKey }}</div>
						<div class="key-preview-hint">点击复制</div>
					</div>
					<van-field
						v-model="addRemark"
						class="add-field"
						placeholder="备注，如：爸爸的手机（可选）"
						clearable
						input-align="center"
					/>
					<van-button
						block
						round
						type="primary"
						class="add-submit"
						:loading="adding"
						@click="addKey"
					>
						添加 Key
					</van-button>
				</section>

				<div v-if="list.length === 0" class="empty-tip">
					<span class="empty-emoji">🗝️</span>
					<span>暂无密钥记录</span>
				</div>

				<div v-else class="card-list">
					<article
						class="key-card"
						:class="{ 'key-card--expanded': isKeyExpanded(item.id) }"
						v-for="item in list"
						:key="item.id"
					>
						<button type="button" class="key-card-head" @click="toggleKeyCard(item.id)">
							<div class="key-card-head-tags">
								<span class="tag tag-remark">{{ item.remark || '未备注' }}</span>
								<span class="tag tag-device">{{ item.device_name || '未绑定' }}</span>
							</div>
							<span class="expand-icon">{{ isKeyExpanded(item.id) ? '▾' : '▸' }}</span>
						</button>
						<div v-show="isKeyExpanded(item.id)" class="key-card-body">
							<button type="button" class="key-block" @click="copyKey(item.auth_key)">
								{{ item.auth_key }}
							</button>
							<div class="key-card-foot">
								<span class="time-text">{{ formatTime(item.create_time) }}</span>
								<div class="action-row">
									<button type="button" class="action-chip" @click="openRemarkDialog(item)">备注</button>
									<button type="button" class="action-chip" @click="copyKey(item.auth_key)">复制</button>
									<button type="button" class="action-chip" @click="queryUpdatesByKey(item.auth_key)">更新</button>
									<button type="button" class="action-chip" @click="queryAccessByKey(item.auth_key)">调用</button>
									<button type="button" class="action-chip action-chip--danger" @click="confirmDelete(item)">删除</button>
								</div>
							</div>
						</div>
					</article>
				</div>
			</div>

			<div v-show="activeTab === 'updates'" class="tab-panel tab-panel--paged">
				<section class="filter-section">
					<van-search
						v-model="updateSearchKey"
						shape="round"
						background="transparent"
						placeholder="按 Key 筛选（留空查全部）"
						show-action
						@search="searchUpdateList"
						@clear="clearUpdateSearch"
					>
						<template #action>
							<div class="search-action" @click="searchUpdateList">查询</div>
						</template>
					</van-search>
				</section>

				<div class="panel-scroll">
					<div v-if="updateLoading && updateList.length === 0" class="empty-tip">加载中...</div>
					<div v-else-if="updateList.length === 0" class="empty-tip">
						<span class="empty-emoji">📋</span>
						<span>暂无更新记录</span>
					</div>

					<div v-else class="card-list">
						<article class="record-card" v-for="item in updateList" :key="item.id">
							<div class="record-card-head">
								<span class="tag tag-remark">{{ item.remark || '未备注' }}</span>
								<span class="time-text">{{ formatTime(item.update_time) }}</span>
							</div>
							<div class="name-change">
								<span class="old-name">{{ item.old_device_name || '未绑定' }}</span>
								<span class="arrow">→</span>
								<span class="new-name">{{ item.new_device_name || '未绑定' }}</span>
							</div>
							<button type="button" class="key-block key-block--sm" @click="copyKey(item.auth_key)">
								{{ item.auth_key }}
							</button>
						</article>
					</div>
				</div>

				<div v-if="updateTotal > 0" class="pagination-wrap">
					<div class="access-pagination">
						<button
							type="button"
							class="page-btn"
							:disabled="updatePage <= 1 || updateLoading"
							@click="goUpdatePage(updatePage - 1)"
						>
							上一页
						</button>
						<div class="page-info">
							<span class="page-current">{{ updatePage }}</span>
							<span class="page-sep">/</span>
							<span class="page-total">{{ updateTotalPages }}</span>
						</div>
						<button
							type="button"
							class="page-btn"
							:disabled="updatePage >= updateTotalPages || updateLoading"
							@click="goUpdatePage(updatePage + 1)"
						>
							下一页
						</button>
					</div>
					<div class="page-summary">共 {{ updateTotal }} 条 · 每页 {{ updatePageSize }} 条</div>
				</div>
			</div>

			<div v-show="activeTab === 'access'" class="tab-panel tab-panel--paged">
				<section class="filter-section">
					<van-search
						v-model="accessSearchKey"
						shape="round"
						background="transparent"
						placeholder="按 Key 筛选（留空查全部）"
						show-action
						@search="searchAccessList"
						@clear="clearAccessSearch"
					>
						<template #action>
							<div class="search-action" @click="searchAccessList">查询</div>
						</template>
					</van-search>
				</section>

				<div class="panel-scroll">
					<div v-if="accessLoading && accessList.length === 0" class="empty-tip">加载中...</div>
					<div v-else-if="accessList.length === 0" class="empty-tip">
						<span class="empty-emoji">📡</span>
						<span>暂无调用记录</span>
					</div>

					<div v-else class="card-list access-card-list">
						<article class="record-card" v-for="item in accessList" :key="item.id">
							<div class="record-card-head">
								<div class="record-card-tags">
									<span :class="['status-tag', item.success ? 'success' : 'fail']">
										{{ item.success ? '成功' : '失败' }}
									</span>
									<span class="tag tag-remark">{{ item.remark || '未备注' }}</span>
								</div>
								<span class="time-text">{{ formatTime(item.access_time) }}</span>
							</div>
							<div class="record-device">{{ item.device_name || '未绑定' }}</div>
							<button type="button" class="key-block key-block--sm" @click="copyKey(item.auth_key)">
								{{ item.auth_key || '—' }}
							</button>
							<div v-if="item.query_id" class="record-extra">查询 id：{{ item.query_id }}</div>
						</article>
					</div>
				</div>

				<div v-if="accessTotal > 0" class="pagination-wrap">
					<div class="access-pagination">
						<button
							type="button"
							class="page-btn"
							:disabled="accessPage <= 1 || accessLoading"
							@click="goAccessPage(accessPage - 1)"
						>
							上一页
						</button>
						<div class="page-info">
							<span class="page-current">{{ accessPage }}</span>
							<span class="page-sep">/</span>
							<span class="page-total">{{ accessTotalPages }}</span>
						</div>
						<button
							type="button"
							class="page-btn"
							:disabled="accessPage >= accessTotalPages || accessLoading"
							@click="goAccessPage(accessPage + 1)"
						>
							下一页
						</button>
					</div>
					<div class="page-summary">共 {{ accessTotal }} 条 · 每页 {{ accessPageSize }} 条</div>
				</div>
			</div>
		</div>

		<van-dialog
			v-model="showRemarkDialog"
			title="编辑备注"
			show-cancel-button
			@confirm="saveRemark"
		>
			<div class="remark-dialog-body">
				<van-field
					v-model="editRemark"
					label="备注"
					placeholder="如：爸爸的手机"
					clearable
				/>
				<div class="remark-dialog-key">{{ remarkItem?.auth_key }}</div>
			</div>
		</van-dialog>

		<van-dialog
			v-model="showDeleteDialog"
			title="删除 Key"
			show-cancel-button
			@confirm="deleteKey"
		>
			<div class="delete-dialog-body">
				<div>确定删除这条 Key 吗？</div>
				<div class="delete-dialog-key">{{ deleteItem?.auth_key }}</div>
			</div>
		</van-dialog>
	</div>
</template>

<script>
import { Notify } from 'vant'
import {
	getDeviceKeyList,
	addDeviceKey,
	deleteDeviceKey,
	getDeviceKeyUpdateList,
	getDeviceKeyAccessList,
	updateDeviceKeyRemark,
} from '../util/api'

function randomKey(length = 32) {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
	const array = new Uint8Array(length)
	crypto.getRandomValues(array)
	let key = ''
	for (let i = 0; i < length; i++) {
		key += chars[array[i] % chars.length]
	}
	return key
}

export default {
	name: 'DeviceKey',
	data() {
		return {
			activeTab: 'keys',
			list: [],
			updateList: [],
			updateSearchKey: '',
			updatePage: 1,
			updatePageSize: 20,
			updateTotal: 0,
			updateLoading: false,
			accessList: [],
			accessSearchKey: '',
			accessPage: 1,
			accessPageSize: 20,
			accessTotal: 0,
			accessLoading: false,
			addRemark: '',
			generatedKey: '',
			adding: false,
			showRemarkDialog: false,
			remarkItem: null,
			editRemark: '',
			savingRemark: false,
			showDeleteDialog: false,
			deleteItem: null,
			deleting: false,
			expandedKeyIds: [],
			tabItems: [
				{ name: 'keys', label: '密钥列表' },
				{ name: 'updates', label: '更新记录' },
				{ name: 'access', label: '调用记录' },
			],
		}
	},
	computed: {
		headerSubText() {
			if (this.activeTab === 'keys') {
				return `共 ${this.list.length} 条`
			}
			if (this.activeTab === 'updates') {
				return `更新记录 共 ${this.updateTotal} 条`
			}
			return `调用记录 共 ${this.accessTotal} 条`
		},
		updateTotalPages() {
			return Math.max(1, Math.ceil(this.updateTotal / this.updatePageSize))
		},
		accessTotalPages() {
			return Math.max(1, Math.ceil(this.accessTotal / this.accessPageSize))
		},
	},
	methods: {
		regenerateKey() {
			this.generatedKey = randomKey()
		},
		loadList() {
			getDeviceKeyList().then((res) => {
				this.list = res.data?.data || []
				this.expandedKeyIds = this.expandedKeyIds.filter((id) =>
					this.list.some((item) => item.id === id)
				)
			})
		},
		normalizePagedResponse(raw, fallbackPageSize = 20) {
			if (Array.isArray(raw)) {
				return {
					list: raw,
					total: raw.length,
					page: 1,
					pageSize: fallbackPageSize,
				}
			}
			if (raw && Array.isArray(raw.list)) {
				return {
					list: raw.list,
					total: Number(raw.total) || raw.list.length,
					page: Number(raw.page) || 1,
					pageSize: Number(raw.pageSize) || fallbackPageSize,
				}
			}
			return {
				list: [],
				total: 0,
				page: 1,
				pageSize: fallbackPageSize,
			}
		},
		applyPagedResult(targetListKey, targetTotalKey, targetPageKey, raw, fallbackPageSize) {
			const payload = this.normalizePagedResponse(raw, fallbackPageSize)
			this[targetListKey] = payload.list
			this[targetTotalKey] = Number(payload.total) || 0
			if (payload.page) {
				this[targetPageKey] = Number(payload.page) || 1
			}
		},
		isKeyExpanded(id) {
			return this.expandedKeyIds.includes(id)
		},
		toggleKeyCard(id) {
			if (this.isKeyExpanded(id)) {
				this.expandedKeyIds = this.expandedKeyIds.filter((itemId) => itemId !== id)
				return
			}
			this.expandedKeyIds = [...this.expandedKeyIds, id]
		},
		loadUpdateList() {
			this.updateLoading = true
			const params = {
				page: this.updatePage,
				pageSize: this.updatePageSize,
			}
			if (this.updateSearchKey && this.updateSearchKey.trim()) {
				params.authKey = this.updateSearchKey.trim()
			}
			getDeviceKeyUpdateList(params)
				.then((res) => {
					if (res.data?.code !== 0 && res.data?.error) {
						this.updateList = []
						this.updateTotal = 0
						return
					}
					this.applyPagedResult(
						'updateList',
						'updateTotal',
						'updatePage',
						res.data?.data,
						this.updatePageSize
					)
				})
				.finally(() => {
					this.updateLoading = false
				})
		},
		searchUpdateList() {
			this.updatePage = 1
			this.loadUpdateList()
		},
		queryUpdatesByKey(authKey) {
			this.updateSearchKey = authKey
			this.updatePage = 1
			this.activeTab = 'updates'
			this.loadUpdateList()
		},
		clearUpdateSearch() {
			this.updateSearchKey = ''
			this.updatePage = 1
			this.loadUpdateList()
		},
		goUpdatePage(page) {
			if (page < 1 || page > this.updateTotalPages || page === this.updatePage) return
			this.updatePage = page
			this.loadUpdateList()
		},
		loadAccessList() {
			this.accessLoading = true
			const params = {
				page: this.accessPage,
				pageSize: this.accessPageSize,
			}
			if (this.accessSearchKey && this.accessSearchKey.trim()) {
				params.authKey = this.accessSearchKey.trim()
			}
			getDeviceKeyAccessList(params)
				.then((res) => {
					if (res.data?.code !== 0 && res.data?.error) {
						this.accessList = []
						this.accessTotal = 0
						return
					}
					this.applyPagedResult(
						'accessList',
						'accessTotal',
						'accessPage',
						res.data?.data,
						this.accessPageSize
					)
				})
				.finally(() => {
					this.accessLoading = false
				})
		},
		searchAccessList() {
			this.accessPage = 1
			this.loadAccessList()
		},
		clearAccessSearch() {
			this.accessSearchKey = ''
			this.accessPage = 1
			this.loadAccessList()
		},
		goAccessPage(page) {
			if (page < 1 || page > this.accessTotalPages || page === this.accessPage) return
			this.accessPage = page
			this.loadAccessList()
		},
		queryAccessByKey(authKey) {
			this.accessSearchKey = authKey
			this.accessPage = 1
			this.activeTab = 'access'
			this.loadAccessList()
		},
		addKey() {
			if (!this.generatedKey) {
				this.regenerateKey()
			}
			this.adding = true
			const payload = { authKey: this.generatedKey }
			if (this.addRemark && this.addRemark.trim()) {
				payload.remark = this.addRemark.trim()
			}
			addDeviceKey(payload)
				.then((res) => {
					if (res.data?.code === 0) {
						Notify({ type: 'success', message: '添加成功' })
						this.loadList()
						this.regenerateKey()
						this.addRemark = ''
					} else {
						Notify({ type: 'danger', message: res.data?.message || '添加失败' })
					}
				})
				.finally(() => {
					this.adding = false
				})
		},
		openRemarkDialog(item) {
			this.remarkItem = item
			this.editRemark = item.remark || ''
			this.showRemarkDialog = true
		},
		saveRemark() {
			if (!this.remarkItem?.id || this.savingRemark) return
			this.savingRemark = true
			updateDeviceKeyRemark({
				id: this.remarkItem.id,
				remark: this.editRemark,
			})
				.then((res) => {
					if (res.data?.code === 0) {
						Notify({ type: 'success', message: '备注已保存' })
						this.loadList()
					} else {
						Notify({ type: 'danger', message: res.data?.message || '保存失败' })
					}
				})
				.finally(() => {
					this.savingRemark = false
					this.remarkItem = null
				})
		},
		confirmDelete(item) {
			this.deleteItem = item
			this.showDeleteDialog = true
		},
		deleteKey() {
			if (!this.deleteItem?.id || this.deleting) return
			this.deleting = true
			deleteDeviceKey({ id: this.deleteItem.id })
				.then((res) => {
					if (res.data?.code === 0) {
						Notify({ type: 'success', message: '删除成功' })
						this.loadList()
					} else {
						Notify({ type: 'danger', message: res.data?.message || '删除失败' })
					}
				})
				.finally(() => {
					this.deleting = false
					this.deleteItem = null
				})
		},
		copyKey(key) {
			if (!key) return
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(key).then(() => {
					Notify({ type: 'success', message: '已复制 Key' })
				}).catch(() => {
					this.fallbackCopy(key)
				})
			} else {
				this.fallbackCopy(key)
			}
		},
		fallbackCopy(text) {
			const input = document.createElement('textarea')
			input.value = text
			document.body.appendChild(input)
			input.select()
			try {
				document.execCommand('copy')
				Notify({ type: 'success', message: '已复制 Key' })
			} catch (e) {
				Notify({ type: 'danger', message: '复制失败' })
			}
			document.body.removeChild(input)
		},
		formatTime(time) {
			if (!time) return ''
			const date = new Date(time)
			if (Number.isNaN(date.getTime())) return time
			const y = date.getFullYear()
			const m = date.getMonth() + 1
			const d = date.getDate()
			const h = String(date.getHours()).padStart(2, '0')
			const min = String(date.getMinutes()).padStart(2, '0')
			const sec = String(date.getSeconds()).padStart(2, '0')
			return `${y}/${m}/${d} ${h}:${min}:${sec}`
		},
	},
	watch: {
		activeTab(tab) {
			if (tab === 'updates') {
				this.loadUpdateList()
			} else if (tab === 'access') {
				this.loadAccessList()
			}
		},
	},
	mounted() {
		this.regenerateKey()
		this.loadList()
	},
}
</script>

<style scoped>
.content {
	--q-bg0: #fff7fb;
	--q-bg1: #f2fbff;
	--q-card: #ffffff;
	--q-line: rgba(15, 23, 42, 0.07);
	--q-shadow-sm: 0 4px 16px rgba(17, 24, 39, 0.06);
	--q-radius: 16px;
	--q-accent: #0891b2;
	--q-accent-soft: rgba(8, 145, 178, 0.1);

	padding: 12px 12px 16px;
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	overflow: hidden;
	box-sizing: border-box;
	font-family: "Microsoft YaHei UI", "PingFang SC", "Hiragino Sans GB", system-ui, -apple-system, sans-serif;
	background: radial-gradient(120% 90% at 20% 0%, var(--q-bg0) 0%, transparent 60%),
		radial-gradient(120% 90% at 90% 30%, var(--q-bg1) 0%, transparent 55%),
		#f8fafc;
}

.page-header {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-shrink: 0;
	margin-bottom: 8px;
	padding: 14px 16px;
	border: 1px solid var(--q-line);
	border-radius: var(--q-radius);
	background: var(--q-card);
	box-shadow: var(--q-shadow-sm);
}

.page-header-emoji {
	font-size: 26px;
	line-height: 1;
}

.page-header-title {
	font-size: 17px;
	font-weight: 700;
	color: #111827;
	letter-spacing: 0.02em;
}

.page-header-sub {
	font-size: 12px;
	color: #94a3b8;
	margin-top: 3px;
}

.page-tabs {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

.tab-bar {
	display: flex;
	flex-shrink: 0;
	margin-bottom: 12px;
	border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.tab-item {
	flex: 1;
	position: relative;
	border: none;
	background: none;
	padding: 2px 4px 12px;
	font-size: 14px;
	color: #94a3b8;
	font-weight: 500;
	line-height: 1.4;
	text-align: center;
}

.tab-item.active {
	color: #0891b2;
	font-weight: 700;
}

.tab-item.active::after {
	content: '';
	position: absolute;
	left: 50%;
	bottom: -1px;
	transform: translateX(-50%);
	width: 28px;
	height: 3px;
	border-radius: 999px 999px 0 0;
	background: linear-gradient(90deg, #0891b2, #34c3ff);
}

.tab-panel {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	padding-bottom: 8px;
}

.tab-panel--paged {
	overflow: hidden;
	padding-bottom: 0;
}

.panel-scroll {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

.panel-scroll .empty-tip {
	min-height: 120px;
}

.panel-scroll .card-list {
	flex: none;
	min-height: auto;
}

.add-section,
.filter-section {
	flex-shrink: 0;
	margin-bottom: 12px;
}

.add-section {
	padding: 14px;
	border: 1px solid var(--q-line);
	border-radius: var(--q-radius);
	background: var(--q-card);
	box-shadow: var(--q-shadow-sm);
}

.add-section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 10px;
}

.add-title {
	font-size: 14px;
	font-weight: 700;
	color: #1e293b;
}

.link-btn {
	border: none;
	background: none;
	padding: 0;
	font-size: 13px;
	font-weight: 600;
	color: var(--q-accent);
}

.key-preview {
	padding: 12px;
	border-radius: 12px;
	background: linear-gradient(135deg, rgba(8, 145, 178, 0.06), rgba(52, 195, 255, 0.08));
	border: 1px solid rgba(8, 145, 178, 0.15);
	margin-bottom: 10px;
	cursor: pointer;
}

.key-preview-text {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 12px;
	line-height: 1.55;
	word-break: break-all;
	color: #0f766e;
	text-align: left;
}

.key-preview-hint {
	margin-top: 6px;
	font-size: 11px;
	color: #94a3b8;
	text-align: center;
}

.add-field {
	margin-bottom: 10px;
	border: 1px solid var(--q-line);
	border-radius: 10px;
	overflow: hidden;
	background: #f8fafc;
}

.add-field >>> .van-cell {
	padding: 10px 14px;
	background: transparent;
}

.add-field >>> .van-field__control {
	text-align: center;
}

.add-field >>> .van-field__control::placeholder {
	text-align: center;
}

.add-submit {
	height: 40px;
	font-weight: 700;
}

.filter-section {
	padding: 0 2px;
}

.filter-section >>> .van-search {
	padding: 0;
}

.filter-section >>> .van-search__content {
	background: var(--q-card);
	border: 1px solid var(--q-line);
	box-shadow: var(--q-shadow-sm);
}

.search-action {
	font-size: 14px;
	font-weight: 600;
	color: var(--q-accent);
	padding: 0 4px;
}

.empty-tip {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	color: #94a3b8;
	font-size: 14px;
	min-height: 140px;
}

.empty-emoji {
	font-size: 32px;
	line-height: 1;
}

.card-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
	flex: 1;
	min-height: 0;
}

.key-card,
.record-card {
	padding: 14px;
	border: 1px solid var(--q-line);
	border-radius: var(--q-radius);
	background: var(--q-card);
	box-shadow: var(--q-shadow-sm);
}

.key-card {
	padding: 10px 12px;
}

.key-card--expanded {
	padding-bottom: 14px;
}

.key-card-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	width: 100%;
	margin: 0;
	padding: 4px 2px;
	border: none;
	background: none;
	text-align: left;
	cursor: pointer;
}

.key-card-head-tags {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	flex: 1;
}

.expand-icon {
	flex-shrink: 0;
	font-size: 14px;
	color: #94a3b8;
	line-height: 1;
}

.key-card-body {
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px dashed var(--q-line);
}

.record-card-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin-bottom: 10px;
}

.record-card-tags {
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
	min-width: 0;
}

.tag {
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	padding: 4px 10px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.3;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.tag-remark {
	background: rgba(15, 118, 110, 0.1);
	color: #0f766e;
}

.tag-device {
	background: #f1f5f9;
	color: #475569;
	max-width: 160px;
}

.status-tag {
	padding: 4px 10px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 700;
	flex-shrink: 0;
}

.status-tag.success {
	color: #047857;
	background: rgba(16, 185, 129, 0.12);
}

.status-tag.fail {
	color: #b91c1c;
	background: rgba(239, 68, 68, 0.12);
}

.key-block {
	display: block;
	width: 100%;
	padding: 10px 12px;
	border: 1px solid rgba(15, 23, 42, 0.06);
	border-radius: 10px;
	background: #f8fafc;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 12px;
	line-height: 1.5;
	color: #334155;
	word-break: break-all;
	text-align: left;
	cursor: pointer;
}

.key-block--sm {
	font-size: 11px;
	padding: 8px 10px;
}

.key-card-foot {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px dashed var(--q-line);
}

.time-text {
	font-size: 11px;
	color: #94a3b8;
	font-variant-numeric: tabular-nums;
	flex-shrink: 0;
}

.action-row {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.action-chip {
	border: 1px solid rgba(8, 145, 178, 0.2);
	background: var(--q-accent-soft);
	color: var(--q-accent);
	padding: 5px 10px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 600;
	line-height: 1;
}

.action-chip--danger {
	border-color: rgba(239, 68, 68, 0.2);
	background: rgba(239, 68, 68, 0.08);
	color: #dc2626;
}

.name-change {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 10px;
	font-size: 14px;
}

.old-name {
	color: #94a3b8;
}

.new-name {
	color: #111827;
	font-weight: 700;
}

.arrow {
	color: var(--q-accent);
	font-weight: 700;
}

.record-device {
	font-size: 14px;
	font-weight: 600;
	color: #1e293b;
	margin-bottom: 8px;
	word-break: break-all;
}

.record-extra {
	margin-top: 8px;
	font-size: 11px;
	color: #94a3b8;
}

.access-card-list {
	margin-bottom: 0;
}

.pagination-wrap {
	flex-shrink: 0;
	margin-top: 0;
	padding: 10px 0 6px;
	background: linear-gradient(180deg, rgba(248, 250, 252, 0) 0%, #f8fafc 24%);
	border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.access-pagination {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 12px 14px;
	border: 1px solid var(--q-line);
	border-radius: var(--q-radius);
	background: var(--q-card);
	box-shadow: var(--q-shadow-sm);
}

.page-btn {
	flex: 1;
	max-width: 108px;
	height: 36px;
	border: 1px solid rgba(8, 145, 178, 0.25);
	border-radius: 999px;
	background: var(--q-accent-soft);
	color: var(--q-accent);
	font-size: 13px;
	font-weight: 700;
}

.page-btn:active:not(:disabled) {
	transform: scale(0.98);
}

.page-btn:disabled {
	opacity: 0.38;
}

.page-info {
	display: flex;
	align-items: baseline;
	justify-content: center;
	gap: 4px;
	min-width: 72px;
	font-variant-numeric: tabular-nums;
}

.page-current {
	font-size: 20px;
	font-weight: 800;
	color: #111827;
	line-height: 1;
}

.page-sep {
	font-size: 13px;
	color: #d1d5db;
	font-weight: 600;
}

.page-total {
	font-size: 14px;
	font-weight: 700;
	color: #6b7280;
}

.page-summary {
	margin-top: 8px;
	text-align: center;
	font-size: 11px;
	color: #94a3b8;
}

.remark-dialog-body {
	padding: 8px 0 16px;
}

.remark-dialog-key {
	padding: 0 16px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 11px;
	color: #94a3b8;
	word-break: break-all;
}

.delete-dialog-body {
	padding: 16px 20px;
	font-size: 14px;
	color: #374151;
	line-height: 1.6;
}

.delete-dialog-key {
	margin-top: 8px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 12px;
	color: #64748b;
	word-break: break-all;
}
</style>
